/**
 * Themed connect control — replaces wallet-adapter-react-ui's WalletMultiButton.
 *
 *  - Disconnected: a pump.fun-styled "Connect Wallet" button that opens our
 *    own <ConnectModal/> (the wallet's approval popup itself is never ours to
 *    style — see ConnectModal header comment).
 *  - Connected: a pill showing the address + live rapid-fire (auto-approve)
 *    status, with a menu to copy the address, arm/disarm rapid-fire, or
 *    disconnect.
 *
 * Auto-approve arming runs HERE (not in the modal) once `connected + publicKey`
 * are live, so `delegateSessionKey` signs with a fresh wallet snapshot rather
 * than a stale just-connected closure.
 */
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import React, { useCallback, useEffect, useState } from "react";

import { resolveBrand, type Brand } from "../launch/brand";
import { useLaunchTheme } from "../launch/LaunchProvider";
import {
  delegateSessionKey,
  getActiveSessionKey,
  revokeSessionKey,
  subscribeSessionKeyChange,
} from "../session/sessionKey";
import { ConnectModal, friendlyConnectError } from "./ConnectModal";

export function ConnectButton(): JSX.Element {
  const theme = useLaunchTheme();
  const brand = theme.brand ?? resolveBrand();
  const a = brand.accent;

  const { connection } = useConnection();
  const walletCtx = useWallet();
  const { connected, publicKey, disconnect } = walletCtx;

  const [modalOpen, setModalOpen] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const [pendingArm, setPendingArm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Re-read session-key status on store changes + a 1s countdown tick.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const unsub = subscribeSessionKeyChange(() => forceTick((n) => n + 1));
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => {
      unsub();
      clearInterval(id);
    };
  }, []);

  // Close modal the moment we're connected.
  useEffect(() => {
    if (connected) setModalOpen(false);
  }, [connected]);

  // Arm rapid-fire after a modal connect that requested it. Deps include
  // publicKey so this fires with a live, signable wallet snapshot.
  useEffect(() => {
    if (!pendingArm || !connected || !publicKey) return;
    setPendingArm(false);
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await delegateSessionKey(connection, walletCtx);
      } catch (e) {
        setError(friendlyConnectError(e));
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingArm, connected, publicKey]);

  const active = publicKey ? getActiveSessionKey(publicKey) : null;
  const remainingMin = active
    ? Math.max(0, Math.ceil((active.expiresAt - Math.floor(Date.now() / 1000)) / 60))
    : 0;

  const arm = useCallback(async (): Promise<void> => {
    if (!connected || !publicKey) return;
    setBusy(true);
    setError(null);
    setMenuOpen(false);
    try {
      await delegateSessionKey(connection, walletCtx);
    } catch (e) {
      setError(friendlyConnectError(e));
    } finally {
      setBusy(false);
    }
  }, [connection, walletCtx, connected, publicKey]);

  const disarm = useCallback(async (): Promise<void> => {
    if (!connected || !publicKey) return;
    setBusy(true);
    setError(null);
    setMenuOpen(false);
    try {
      await revokeSessionKey(connection, walletCtx);
    } catch (e) {
      setError(friendlyConnectError(e));
    } finally {
      setBusy(false);
    }
  }, [connection, walletCtx, connected, publicKey]);

  if (!connected || !publicKey) {
    return (
      <>
        <button onClick={() => setModalOpen(true)} style={connectBtnStyle(a)}>
          Connect Wallet
        </button>
        {modalOpen && (
          <ConnectModal
            brand={brand}
            onClose={() => setModalOpen(false)}
            autoApprove={autoApprove}
            onAutoApproveChange={setAutoApprove}
            onConnectInitiated={(v) => setPendingArm(v)}
          />
        )}
      </>
    );
  }

  const addr = publicKey.toBase58();
  const short = `${addr.slice(0, 4)}…${addr.slice(-4)}`;

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setMenuOpen((o) => !o)} style={pillStyle(a)} disabled={busy}>
        <img src={brand.iconUrl} alt="" width={18} height={18} />
        <span style={{ fontWeight: 600 }}>{short}</span>
        <span
          title={active ? `rapid-fire on · ${remainingMin}m left` : "rapid-fire off"}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: active ? a.primary : "#5a5a68",
            boxShadow: active ? `0 0 6px ${a.primary}` : "none",
          }}
        />
      </button>

      {menuOpen && (
        <div style={menuStyle(a)} onMouseLeave={() => setMenuOpen(false)}>
          <div style={{ fontSize: 11, color: a.subtext, padding: "2px 4px 8px" }}>
            {active ? `⚡ rapid-fire on · ${remainingMin}m left` : "rapid-fire off"}
          </div>
          <button
            style={menuItemStyle(a)}
            onClick={() => {
              void navigator.clipboard?.writeText(addr);
              setMenuOpen(false);
            }}
          >
            Copy address
          </button>
          {active ? (
            <button style={menuItemStyle(a)} disabled={busy} onClick={() => void disarm()}>
              Disable auto-approve
            </button>
          ) : (
            <button
              style={{ ...menuItemStyle(a), color: a.primary }}
              disabled={busy}
              onClick={() => void arm()}
            >
              ⚡ Enable auto-approve (15 min)
            </button>
          )}
          <button
            style={{ ...menuItemStyle(a), color: "#f4a4b4" }}
            onClick={() => {
              setMenuOpen(false);
              void disconnect();
            }}
          >
            Disconnect
          </button>
        </div>
      )}

      {error && (
        <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, color: "#f4a4b4", fontSize: 11, maxWidth: 220 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// --- styles -----------------------------------------------------------------

function connectBtnStyle(a: Brand["accent"]): React.CSSProperties {
  return {
    padding: "9px 18px",
    background: a.cta,
    color: "#1a1505",
    border: "none",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
    pointerEvents: "auto",
  };
}

function pillStyle(a: Brand["accent"]): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 12px",
    background: a.card,
    color: a.text,
    border: `1px solid ${a.border}`,
    borderRadius: 999,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
    pointerEvents: "auto",
  };
}

function menuStyle(a: Brand["accent"]): React.CSSProperties {
  return {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 6,
    minWidth: 200,
    background: a.card,
    border: `1px solid ${a.border}`,
    borderRadius: 12,
    padding: 6,
    boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
    zIndex: 1000,
    pointerEvents: "auto",
  };
}

function menuItemStyle(a: Brand["accent"]): React.CSSProperties {
  return {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "8px 10px",
    background: "transparent",
    color: a.text,
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
  };
}
